import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import ContactInfo from './ContactInfo';
import { formatTimeDisplay } from '../../../../lib/utils/dateTimeFormat';
import EditBooking from './editBooking/EditBooking';
import './BookingsManagement.css';

/**
 * BookingTableRow Component - Renders a single row in the bookings table
 * @param {Object} props - Component props
 * @param {Object} props.booking - The booking data to display
 * @param {Function} props.onStatusChange - Function to handle status changes
 * @param {Function} props.onDelete - Function to handle booking deletion
 * @param {Function} props.onClick - Function to handle row click
 * @param {Function} props.onEdit - Function to handle booking edit
 * @returns {JSX.Element} - Rendered component
 */
function BookingTableRow({ booking, onStatusChange, onDelete, onClick, onEdit }) {
  const [showEditModal, setShowEditModal] = useState(false);

  // Determine customer name based on whether it's a guest or registered user
  const isGuestBooking = booking.guest_profile_id && !booking.user_id;
  const customerName = isGuestBooking 
    ? `${booking.guest_profiles?.first_name || ''} ${booking.guest_profiles?.last_name || ''}`
    : `${booking.profiles?.first_name || ''} ${booking.profiles?.last_name || ''}`;

  const handleRowClick = (e) => {
    // Don't trigger row click if clicking on status select or buttons
    if (e.target.tagName === 'SELECT' || 
        e.target.className.includes('delete-btn') || 
        e.target.className.includes('edit-btn')) {
      return;
    }
    onClick();
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setShowEditModal(true);
  };

  const handleEditSave = async (bookingId, updatedData) => {
    try {
      await onEdit(bookingId, updatedData);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  return (
    <>
      <tr key={booking.id} onClick={handleRowClick} className="booking-row">
        <td>
          {customerName.trim() ? customerName : 'N/A'}
          {isGuestBooking && <span className="guest-badge"></span>}
        </td>
        <td>{booking.vehicle_types?.title || 'N/A'}</td>
        <td>{booking.services?.name || 'N/A'}</td>
        <td>{booking.booking_date}</td>
        <td>{formatTimeDisplay(booking.booking_time)}</td>
        <td>
          <select
            value={booking.status}
            onChange={(e) => onStatusChange(booking.id, e.target.value)}
            className={`status-select status-${booking.status.toLowerCase()}`}
          >
            <option value="Pending">Pending</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </td>
        <td>${booking.total_price}</td>
        <td className="bookings-management__contact-cell">
          <ContactInfo booking={booking} />
        </td>
        <td>
          <div className="actions-btn-container">
            <button
              className="edit-btn"
              onClick={handleEditClick}
            >
              Edit
            </button>
            <button
              className="delete-btn"
              onClick={() => onDelete(booking.id)}
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {showEditModal && ReactDOM.createPortal(
        <div className="edit-modal-container">
          <EditBooking
            booking={booking}
            onClose={() => setShowEditModal(false)}
            onSave={handleEditSave}
          />
        </div>,
        document.body
      )}
    </>
  );
}

export default BookingTableRow; 